import { API, Endpoints } from '@brg/api'
import type { LocalizedName } from './managerTestItem'

export type QuestionType = number

export type ExplanatoryFileItem = {
  localization: string
  isOptimized: boolean
  maskNumber: number
  originalFileId: string
}

export type ManagerQuestionFileSlotPayload = {
  fileId: string
  isNew?: boolean
}

export type ManagerQuestionAnswerOptionPayload = {
  text: LocalizedName[]
  isCorrectAnswer: boolean
  position: number
  explanatoryText: LocalizedName[]
  explanatoryFileContainerId: string
  explanatoryFiles: ExplanatoryFileItem[]
  files: ManagerQuestionFileSlotPayload[]
}

export type QuestionScaleType = 0 | 1 | 2

export type ManagerQuestionAddPayload = {
  id: string
  text: LocalizedName[]
  type: QuestionType
  position: number
  answerOptions: ManagerQuestionAnswerOptionPayload[]
  testItemId: string
  created: string
  updated: string
  files: ManagerQuestionFileSlotPayload[]
  scaleType?: QuestionScaleType
}

export type AnswerOptionDraft = {
  text: string
  correct: boolean
  optionFileId?: string
  optionImageIsNew?: boolean
}

export type QuestionDraftForApi = {
  id: string
  text: string
  answerMode: 'single' | 'multiple'
  /** When set, overrides answerMode→type mapping (questionnaire extended types). */
  questionTypeCode?: number
  options: AnswerOptionDraft[]
  explanation?: string
  questionFileId?: string
  questionImageIsNew?: boolean
}

function bilingualSame(text: string): LocalizedName[] {
  const t = String(text ?? '')
  return [
    { lang: 'ru', name: t },
    { lang: 'en', name: t }
  ]
}

function bilingualEmpty(): LocalizedName[] {
  return [
    { lang: 'ru', name: '' },
    { lang: 'en', name: '' }
  ]
}

function buildImageFilesPayload(
  fileId: string | undefined,
  isNewUpload: boolean
): ManagerQuestionFileSlotPayload[] {
  const id = String(fileId ?? '').trim()
  if (!id) {
    return []
  }
  if (isNewUpload) {
    return [{ fileId: id, isNew: true }]
  }
  return [{ fileId: id }]
}

export function buildManagerQuestionAddPayload(
  draft: QuestionDraftForApi,
  testItemId: string,
  position: number
): ManagerQuestionAddPayload {
  const now = new Date().toISOString()
  let type: QuestionType = draft.answerMode === 'single' ? 0 : 1
  if (draft.questionTypeCode != null && Number.isFinite(draft.questionTypeCode)) {
    type = draft.questionTypeCode
  }

  const qFileId = String(draft.questionFileId ?? '').trim()
  const qImageNew = Boolean(draft.questionImageIsNew)
  const explanationText = String(draft.explanation ?? '').trim()

  const answerOptions: ManagerQuestionAnswerOptionPayload[] =
    draft.options.map((opt, index) => {
      const oFileId = String(opt.optionFileId ?? '').trim()
      return {
        text: bilingualSame(opt.text),
        isCorrectAnswer: opt.correct,
        position: index,
        explanatoryText:
          opt.correct && explanationText
            ? bilingualSame(draft.explanation ?? '')
            : bilingualEmpty(),
        explanatoryFileContainerId: '',
        explanatoryFiles: [],
        files: buildImageFilesPayload(oFileId, Boolean(opt.optionImageIsNew))
      }
    })

  return {
    id: draft.id,
    text: bilingualSame(draft.text),
    type,
    position,
    answerOptions,
    testItemId,
    created: now,
    updated: now,
    files: buildImageFilesPayload(qFileId, qImageNew)
  }
}

export type AddManagerQuestionOptions = {
  accessToken?: string | null
}

export type FetchManagerQuestionListOptions = {
  accessToken?: string | null
}

/** Ответ GetList: массив или обёртка `{ data: [...] }`. */
export function unwrapManagerQuestionListPayload(raw: unknown): unknown[] {
  if (Array.isArray(raw)) {
    return raw
  }
  if (raw !== null && typeof raw === 'object') {
    const d = (raw as Record<string, unknown>).data
    if (Array.isArray(d)) {
      return d
    }
  }
  return []
}

export async function fetchManagerQuestionList(
  testItemId: string,
  options?: FetchManagerQuestionListOptions
): Promise<unknown> {
  const id = String(testItemId ?? '').trim()
  if (!id) {
    throw new Error('testItemId required')
  }

  let req = API.request()
    .endpoint(Endpoints.gateway)
    .url('testsservice/api/ManagerQuestion/GetList')
    .addQuery('testItemId', id)
    .get()

  const token = options?.accessToken?.trim()
  if (token) {
    req = req.headers({ Authorization: `Bearer ${token}` })
  }

  const response = await req.send()
  const text = await response.text()

  if (!response.ok) {
    let msg = `HTTP ${response.status}`
    if (text.trim()) {
      try {
        const errBody = JSON.parse(text) as Record<string, unknown>
        const title = errBody.title
        const message = errBody.message ?? errBody.error
        if (typeof title === 'string') {
          msg = title
        } else if (typeof message === 'string') {
          msg = message
        }
      } catch {
        // keep msg
      }
    }
    throw new Error(msg)
  }

  if (!text.trim()) {
    return []
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    throw new Error('Invalid JSON response')
  }
}

async function sendManagerQuestionRequest(
  path: 'testsservice/api/ManagerQuestion/Add' | 'testsservice/api/ManagerQuestion/Update',
  payload: ManagerQuestionAddPayload,
  options?: AddManagerQuestionOptions
): Promise<unknown> {
  let req = API.request()
    .endpoint(Endpoints.gateway)
    .url(path)
    .post()
    .body(payload)

  const token = options?.accessToken?.trim()
  if (token) {
    req = req.headers({ Authorization: `Bearer ${token}` })
  }

  const response = await req.send()
  const text = await response.text()

  if (!response.ok) {
    let msg = `HTTP ${response.status}`
    if (text.trim()) {
      try {
        const errBody = JSON.parse(text) as Record<string, unknown>
        const title = errBody.title
        const message = errBody.message ?? errBody.error
        if (typeof title === 'string') {
          msg = title
        } else if (typeof message === 'string') {
          msg = message
        }
      } catch {
      }
    }
    throw new Error(msg)
  }

  if (!text.trim()) {
    return {}
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    throw new Error('Invalid JSON response')
  }
}

export async function addManagerQuestion(
  payload: ManagerQuestionAddPayload,
  options?: AddManagerQuestionOptions
): Promise<unknown> {
  return sendManagerQuestionRequest(
    'testsservice/api/ManagerQuestion/Add',
    payload,
    options
  )
}

export async function updateManagerQuestion(
  payload: ManagerQuestionAddPayload,
  options?: AddManagerQuestionOptions
): Promise<unknown> {
  return sendManagerQuestionRequest(
    'testsservice/api/ManagerQuestion/Update',
    payload,
    options
  )
}

export function shouldUseManagerQuestionUpdate(options: {
  id: string
  serverQuestionId?: string | null
}): boolean {
  const sid = options.serverQuestionId?.trim()
  if (sid) {
    return true
  }
  const id = options.id?.trim() ?? ''
  if (!id) {
    return false
  }
  return !id.startsWith('q-')
}

export function parseManagerQuestionAddResponse(data: unknown): string | null {
  if (data === null || typeof data !== 'object') return null
  const o = data as Record<string, unknown>
  if (typeof o.questionId === 'string' && o.questionId.trim()) {
    return o.questionId.trim()
  }
  if (typeof o.id === 'string' && o.id.trim()) {
    return o.id.trim()
  }
  const dataField = o.data
  if (dataField !== null && typeof dataField === 'object') {
    const d = dataField as Record<string, unknown>
    if (typeof d.questionId === 'string' && d.questionId.trim()) {
      return d.questionId.trim()
    }
    if (typeof d.id === 'string' && d.id.trim()) {
      return d.id.trim()
    }
  }
  return null
}
import { API, Endpoints } from '@brg/api'
import type { LocalizedName } from './managerTestItem'

export type QuestionType = number

export type ExplanatoryFileItem = {
  localization: string
  isOptimized: boolean
  maskNumber: number
  originalFileId: string
}

export type ManagerQuestionFileSlotPayload = {
  fileId: string
  isNew?: boolean
}

export type ManagerQuestionAnswerOptionPayload = {
  text: LocalizedName[]
  isCorrectAnswer: boolean
  position: number
  explanatoryText: LocalizedName[]
  explanatoryFileContainerId: string
  explanatoryFiles: ExplanatoryFileItem[]
  files: ManagerQuestionFileSlotPayload[]
}

export type QuestionScaleType = 0 | 1 | 2

export type ManagerQuestionAddPayload = {
  id: string
  text: LocalizedName[]
  type: QuestionType
  position: number
  answerOptions: ManagerQuestionAnswerOptionPayload[]
  testItemId: string
  created: string
  updated: string
  files: ManagerQuestionFileSlotPayload[]
  scaleType?: QuestionScaleType
}

export type AnswerOptionDraft = {
  text: string
  correct: boolean
  optionFileId?: string
  optionImageIsNew?: boolean
}

export type QuestionDraftForApi = {
  id: string
  text: string
  answerMode: 'single' | 'multiple'
  /** When set, overrides answerMode→type mapping (questionnaire extended types). */
  questionTypeCode?: number
  options: AnswerOptionDraft[]
  explanation?: string
  questionFileId?: string
  questionImageIsNew?: boolean
}

function bilingualSame(text: string): LocalizedName[] {
  const t = String(text ?? '')
  return [
    { lang: 'ru', name: t },
    { lang: 'en', name: t }
  ]
}

function bilingualEmpty(): LocalizedName[] {
  return [
    { lang: 'ru', name: '' },
    { lang: 'en', name: '' }
  ]
}

function buildImageFilesPayload(
  fileId: string | undefined,
  isNewUpload: boolean
): ManagerQuestionFileSlotPayload[] {
  const id = String(fileId ?? '').trim()
  if (!id) {
    return []
  }
  if (isNewUpload) {
    return [{ fileId: id, isNew: true }]
  }
  return [{ fileId: id }]
}

export function buildManagerQuestionAddPayload(
  draft: QuestionDraftForApi,
  testItemId: string,
  position: number
): ManagerQuestionAddPayload {
  const now = new Date().toISOString()
  let type: QuestionType = draft.answerMode === 'single' ? 0 : 1
  if (draft.questionTypeCode != null && Number.isFinite(draft.questionTypeCode)) {
    type = draft.questionTypeCode
  }

  const qFileId = String(draft.questionFileId ?? '').trim()
  const qImageNew = Boolean(draft.questionImageIsNew)
  const explanationText = String(draft.explanation ?? '').trim()

  const answerOptions: ManagerQuestionAnswerOptionPayload[] =
    draft.options.map((opt, index) => {
      const oFileId = String(opt.optionFileId ?? '').trim()
      return {
        text: bilingualSame(opt.text),
        isCorrectAnswer: opt.correct,
        position: index,
        explanatoryText:
          opt.correct && explanationText
            ? bilingualSame(draft.explanation ?? '')
            : bilingualEmpty(),
        explanatoryFileContainerId: '',
        explanatoryFiles: [],
        files: buildImageFilesPayload(oFileId, Boolean(opt.optionImageIsNew))
      }
    })

  return {
    id: draft.id,
    text: bilingualSame(draft.text),
    type,
    position,
    answerOptions,
    testItemId,
    created: now,
    updated: now,
    files: buildImageFilesPayload(qFileId, qImageNew)
  }
}

export type AddManagerQuestionOptions = {
  accessToken?: string | null
}

export type FetchManagerQuestionListOptions = {
  accessToken?: string | null
}

/** Ответ GetList: массив или обёртка `{ data: [...] }`. */
export function unwrapManagerQuestionListPayload(raw: unknown): unknown[] {
  if (Array.isArray(raw)) {
    return raw
  }
  if (raw !== null && typeof raw === 'object') {
    const d = (raw as Record<string, unknown>).data
    if (Array.isArray(d)) {
      return d
    }
  }
  return []
}

export async function fetchManagerQuestionList(
  testItemId: string,
  options?: FetchManagerQuestionListOptions
): Promise<unknown> {
  const id = String(testItemId ?? '').trim()
  if (!id) {
    throw new Error('testItemId required')
  }

  let req = API.request()
    .endpoint(Endpoints.gateway)
    .url('testsservice/api/ManagerQuestion/GetList')
    .addQuery('testItemId', id)
    .get()

  const token = options?.accessToken?.trim()
  if (token) {
    req = req.headers({ Authorization: `Bearer ${token}` })
  }

  const response = await req.send()
  const text = await response.text()

  if (!response.ok) {
    let msg = `HTTP ${response.status}`
    if (text.trim()) {
      try {
        const errBody = JSON.parse(text) as Record<string, unknown>
        const title = errBody.title
        const message = errBody.message ?? errBody.error
        if (typeof title === 'string') {
          msg = title
        } else if (typeof message === 'string') {
          msg = message
        }
      } catch {
        // keep msg
      }
    }
    throw new Error(msg)
  }

  if (!text.trim()) {
    return []
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    throw new Error('Invalid JSON response')
  }
}

async function sendManagerQuestionRequest(
  path: 'testsservice/api/ManagerQuestion/Add' | 'testsservice/api/ManagerQuestion/Update',
  payload: ManagerQuestionAddPayload,
  options?: AddManagerQuestionOptions
): Promise<unknown> {
  let req = API.request()
    .endpoint(Endpoints.gateway)
    .url(path)
    .post()
    .body(payload)

  const token = options?.accessToken?.trim()
  if (token) {
    req = req.headers({ Authorization: `Bearer ${token}` })
  }

  const response = await req.send()
  const text = await response.text()

  if (!response.ok) {
    let msg = `HTTP ${response.status}`
    if (text.trim()) {
      try {
        const errBody = JSON.parse(text) as Record<string, unknown>
        const title = errBody.title
        const message = errBody.message ?? errBody.error
        if (typeof title === 'string') {
          msg = title
        } else if (typeof message === 'string') {
          msg = message
        }
      } catch {
      }
    }
    throw new Error(msg)
  }

  if (!text.trim()) {
    return {}
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    throw new Error('Invalid JSON response')
  }
}

export async function addManagerQuestion(
  payload: ManagerQuestionAddPayload,
  options?: AddManagerQuestionOptions
): Promise<unknown> {
  return sendManagerQuestionRequest(
    'testsservice/api/ManagerQuestion/Add',
    payload,
    options
  )
}

export async function updateManagerQuestion(
  payload: ManagerQuestionAddPayload,
  options?: AddManagerQuestionOptions
): Promise<unknown> {
  return sendManagerQuestionRequest(
    'testsservice/api/ManagerQuestion/Update',
    payload,
    options
  )
}

export function shouldUseManagerQuestionUpdate(options: {
  id: string
  serverQuestionId?: string | null
}): boolean {
  const sid = options.serverQuestionId?.trim()
  if (sid) {
    return true
  }
  const id = options.id?.trim() ?? ''
  if (!id) {
    return false
  }
  return !id.startsWith('q-')
}

export function parseManagerQuestionAddResponse(data: unknown): string | null {
  if (data === null || typeof data !== 'object') return null
  const o = data as Record<string, unknown>
  if (typeof o.questionId === 'string' && o.questionId.trim()) {
    return o.questionId.trim()
  }
  if (typeof o.id === 'string' && o.id.trim()) {
    return o.id.trim()
  }
  const dataField = o.data
  if (dataField !== null && typeof dataField === 'object') {
    const d = dataField as Record<string, unknown>
    if (typeof d.questionId === 'string' && d.questionId.trim()) {
      return d.questionId.trim()
    }
    if (typeof d.id === 'string' && d.id.trim()) {
      return d.id.trim()
    }
  }
  return null
}
